'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { useClientHasRendered } from '../../../../helpers/hooks';
import {
  useIsMobileScreenWidthOrThinner,
  useIsTabletScreenWidthOrThinner,
} from '../../../../helpers/hooks/useScreenWidth';
import {
  GetFishingWater_fishingWater,
  GetFishingWater_fishingWater_country,
  GetFishingWater_fishingWater_region,
  GetFishingWaterNearbyWaters_fishingWater_suggestedWaters,
  GetRegion_entry_country,
  GetPlace_entry_country,
  GetPlace_entry_region,
  GetRegion_entry,
} from '../../../../interfaces/graphql';
import { Button } from '../../../Clickables/Buttons/Button';
import { Container } from '../../../Container/Container';
import { Heading } from '../../../Heading/Heading';
import { LocationLinks } from '../../../LocationLinks/LocationLinks';
import { Source } from '../../../NextImage/Source';
import { Text } from '../../../Text/Text';
import MapPlaceholderDesktop from './assets/mapPlaceholderDesktop.png';
import MapPlaceholderMobile from './assets/mapPlaceholderMobile.png';
import MapPlaceholderTablet from './assets/mapPlaceholderTablet.png';
import { MapLoadingOverlay } from './components/MapLoadingOverlay';
import { MapSEOLinks } from './components/MapSEOLinks';
import { RatingsAndCatchCount } from './components/RatingsAndCatchCount';
import { useGeolocation } from '../../../../contexts/GeolocationContext/GeolocationContext';
import { twMerge } from 'tailwind-merge';
import { IBoundingBox } from '../../../../interfaces/geo';

const DynamicHeroMap = dynamic(async () => import('./HeroMap').then(mod => mod.MapWrapper), {
  loading: () => <MapLoadingOverlay />,
});

const DynamicMapAnimation = dynamic(
  async () => import('./components/MapAnimation/MapAnimation').then(mod => mod.MapAnimation),
  {
    ssr: false,
    loading: () => <MapLoadingOverlay />,
  },
);

interface IProps {
  bbox?: IBoundingBox;
  country?:
    | GetFishingWater_fishingWater_country
    | GetPlace_entry_country
    | GetRegion_entry_country
    | null;
  className?: string;
  fishingReportSectionId?: string;
  id: string;
  latitude?: number;
  longitude?: number;
  mainFishingWater?: GetFishingWater_fishingWater;
  region?: GetFishingWater_fishingWater_region | GetPlace_entry_region | GetRegion_entry | null;
  reviewSectionId?: string;
  SEOFishingSpots?: GetFishingWaterNearbyWaters_fishingWater_suggestedWaters[];
  sourceId?: string;
  subTitle?: string;
  title: string;
  minZoom?: number;
  maxZoom?: number;
  zoom?: number;
  priority?: boolean;
  placeholderClassName?: string;
}

/**
 * HeroMapSection component displays a map section with dynamic loading capabilities.
 * Clicking on the "Explore map" button will load the map. It shows a placeholder image and text until the map is loaded.
 * Can be used with a main fishingwater which is shown when the map is loaded.
 *
 * @param {IProps} props - The properties for the HeroMapSection component.
 * @param {string} props.id - The unique identifier for the section.
 * @param {GetFishingWater_fishingWater_country | GetPlace_entry_country | null} props.country - The country information.
 * @param {GetFishingWater_fishingWater_region | GetPlace_entry_region | null} props.region - The region information.
 * @param {number} props.latitude - The latitude for the map center.
 * @param {number} props.longitude - The longitude for the map center.
 * @param {GetFishingWater_fishingWater} [props.mainFishingWater] - Optionally the main fishing water to be shown on the map.
 * @param {string} props.title - The title of the section.
 * @param {string} [props.subTitle] - The subtitle of the section (above the title).
 * @param {string} [props.reviewSectionId] - The ID for the review section (optionally, used to scroll to reviews).
 * @param {string} [props.fishingReportSectionId] - The ID for the fishing report section (optionally, used to scroll to reviews).
 * @param {GetFishingWaterNearbyWaters_fishingWater_suggestedWaters[]} [props.SEOFishingSpots] - Fishingwaters used to generate hidden SEO Links.
 * @param {number} [props.zoom=HERO_MAP_DEFAULT_ZOOM] - The initial zoom level for the map.
 *
 * @returns {JSX.Element} The HeroMapSection component.
 */
export const HeroMapSection = ({
  bbox,
  country,
  className,
  fishingReportSectionId,
  id,
  latitude,
  longitude,
  mainFishingWater,
  region,
  SEOFishingSpots,
  sourceId,
  subTitle,
  title,
  reviewSectionId,
  zoom,
  minZoom,
  maxZoom,
  priority = true,
  placeholderClassName,
}: IProps) => {
  const { location } = useGeolocation();
  const mapLongitude = longitude ?? location?.coordinates.longitude;
  const mapLatitude = latitude ?? location?.coordinates.latitude;
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const clientHasRendered = useClientHasRendered();
  const { currentPage } = useAnalytics();
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [loadMap, setLoadMap] = useState<boolean>(false);
  const [placeholderHeight, setPlaceholderHeight] = useState<number>(
    placeholderRef.current?.offsetHeight ?? 0,
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [triggerMapResize, setTriggerMapResize] = useState<boolean>(false);
  const countryName = country?.countryName;
  const countryId = country?.id;
  const regionName = region?.name;
  const regionId = region?.id;

  const showLocationLinks = !!(regionId && regionName && countryId && countryName);

  const allReviews = mainFishingWater?.allReviews;
  const catchesCount = mainFishingWater?.catchesCount;
  const showRatingsAndCatchCount = !!mainFishingWater;

  const isMobileScreen = useIsMobileScreenWidthOrThinner();
  const isTabletScreen = useIsTabletScreenWidthOrThinner();

  const getContainerHeight = (): string => {
    if (!clientHasRendered || placeholderHeight === 0) {
      return 'auto';
    }
    if (isMobileScreen && loadMap && isExpanded) {
      return `${placeholderHeight * 2}px`;
    }
    if (isTabletScreen && loadMap && isExpanded) {
      return `${(placeholderHeight * 3) / 2}px`;
    }
    return `${placeholderHeight}px`;
  };

  useEffect(() => {
    const handleResize = (): void => {
      if (placeholderRef.current?.offsetHeight) {
        setPlaceholderHeight(placeholderRef.current.offsetHeight);
      }
    };

    if (clientHasRendered && placeholderRef.current?.offsetHeight) {
      setPlaceholderHeight(placeholderRef.current.offsetHeight);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clientHasRendered]);

  const handleMapClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'HeroMapSection',
        source_id: sourceId,
        type: 'openMap',
        page: currentPage,
      },
    });
    setLoadMap(true);
    setIsExpanded(true);
    setIsAppDownloadModalOpen(true);
  };

  return (
    <Container
      style={{
        height: getContainerHeight(),
      }}
      onTransitionEnd={e => {
        if (e.propertyName === 'height') {
          setTriggerMapResize(true);
        }
      }}
      id={id}
      className={twMerge(
        `relative mb-6 size-full scroll-mt-(--navigation-scrolltop-offset-mobile) p-0 transition-[height] sm:mb-10 sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-none sm:p-0 md:my-11`,
        className,
      )}
    >
      <div className={`width-full ${loadMap ? 'h-full' : 'h-auto'} md:rounded-2xl`}>
        <div className="relative flex size-full items-center justify-center bg-cover">
          {SEOFishingSpots && <MapSEOLinks SEOFishingSpots={SEOFishingSpots} />}
          {/* Dynamically loaded Map */}
          {loadMap && mapLongitude && mapLatitude && (
            <DynamicHeroMap
              bbox={bbox}
              latitude={mapLatitude}
              longitude={mapLongitude}
              mainFishingWater={mainFishingWater}
              fishingReportSectionId={fishingReportSectionId}
              triggerMapResize={triggerMapResize}
              setTriggerMapResize={setTriggerMapResize}
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              sourceId={sourceId}
              minZoom={minZoom}
              maxZoom={maxZoom}
              zoom={zoom}
            />
          )}
          {/* Placeholder Image */}
          <div className="absolute inset-0 overflow-hidden md:rounded-2xl">
            <picture className="absolute inset-0">
              <Source
                media="(min-width: 990px)"
                src={MapPlaceholderDesktop}
                sizes="(min-width: 1680px) 1552px, 100vw"
              />
              <Source
                media="(min-width: 600px)"
                src={MapPlaceholderTablet}
                sizes="(min-width: 1680px) 1552px, 100vw"
              />
              <Image
                fill
                priority={priority}
                alt="Map"
                src={MapPlaceholderMobile}
                className="size-full object-cover md:rounded-2xl"
                sizes="(min-width: 1680px) 1552px, 100vw"
              />
            </picture>
          </div>
          {/* Placeholder Text */}
          <div ref={placeholderRef} className="z-mid flex w-full items-center justify-center">
            <div
              className={twMerge(
                `${loadMap ? 'opacity-0' : 'opacity-100'} mx-8 my-7 flex w-full flex-col items-center justify-center ${showRatingsAndCatchCount ? 'gap-3 py-4 md:gap-6' : 'gap-4 py-6 md:gap-6'} rounded-xl bg-white px-6 sm:mx-28 sm:my-16 sm:py-8 md:my-24 md:max-w-[650px]`,
                placeholderClassName,
              )}
            >
              <div
                className={`flex flex-col items-center justify-center ${showRatingsAndCatchCount ? 'gap-1' : 'gap-2 md:gap-3'}`}
              >
                <Heading
                  className={`m-0 text-center ${showRatingsAndCatchCount ? 'order-2' : 'order-1'}`}
                  as="h1"
                >
                  {title}
                </Heading>
                {subTitle && (
                  <Text
                    variant="xs"
                    className={`m-0 text-center ${showRatingsAndCatchCount ? 'order-1' : 'order-2'}`}
                  >
                    {subTitle}
                  </Text>
                )}
                {showLocationLinks && (
                  <LocationLinks
                    regionId={regionId}
                    regionName={regionName}
                    countryId={countryId}
                    countryName={countryName}
                    className={`order-3 ${showRatingsAndCatchCount ? 'mt-2 md:mt-0' : ''}`}
                  />
                )}
              </div>

              {showRatingsAndCatchCount && (
                <RatingsAndCatchCount
                  allReviews={allReviews ?? null}
                  catchesCount={catchesCount ?? 0}
                  reviewSectionId={reviewSectionId}
                />
              )}
              <Button
                size="sm"
                className={`${showRatingsAndCatchCount ? 'sm:mt-1 md:mt-0' : ''} w-[129px] px-1.5 py-[9px] text-sm md:w-[213px] md:p-3 md:text-base`}
                onClick={handleMapClick}
              >
                Explore map
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isAppDownloadModalOpen && (
        <DynamicMapAnimation
          isAppDownloadModalOpen={isAppDownloadModalOpen}
          setIsAppDownloadModalOpen={setIsAppDownloadModalOpen}
          sourceId={sourceId}
        />
      )}
    </Container>
  );
};
